import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import { colors, fonts, type, pageGeometry, brand, BRAND_ACCENT } from '../theme'
import { Rule, WireLogo } from '../primitives'

// ── Types ─────────────────────────────────────────────────────────────────────

export type ReportStat = {
  label: string
  value: string
  sub?: string
}

export type ReportColumn = {
  key: string
  label: string
  flex?: number          // default 1 — relative flex weight
  align?: 'left' | 'right'  // default 'left'
}

export type ReportSection = {
  label: string
  columns: ReportColumn[]
  rows: Array<Record<string, string | number>>
  chartImageUrl?: string   // base64 PNG to render above the table
  chartHeight?: number     // pt, default 200
}

export type ReadershipsReportData = {
  viewLabel: string   // "By Date" | "By Page" | "By Article"
  subtitle?: string   // page title or article title
  dateRange: string   // e.g. "2026-05-16 – 2026-06-15"
  stats: ReportStat[]
  sections: ReportSection[]
}

export type ReadershipsReportProps = {
  data: ReadershipsReportData
  generatedAt: Date
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    backgroundColor: colors.canvas,
    paddingTop:    pageGeometry.marginTop,
    paddingBottom: pageGeometry.marginBottom,
    paddingLeft:   pageGeometry.marginLeft,
    paddingRight:  pageGeometry.marginRight,
  },

  // ── Header ────────────────────────────────────────────────────────────────
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandLockup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  brandName: {
    fontFamily: fonts.sans,
    fontSize: type.size.xl,
    fontWeight: type.weight.bold,
    color: BRAND_ACCENT,
    letterSpacing: 2,
  },
  headerRight: {
    flexGrow: 1,
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontFamily: fonts.sans,
    fontSize: type.size.sm,
    fontWeight: type.weight.bold,
    color: colors.fgSecondary,
    letterSpacing: 0.5,
  },
  headerDate: {
    fontFamily: fonts.sans,
    fontSize: type.size.xs,
    color: colors.fgMuted,
    marginTop: 2,
  },

  // ── Context block ─────────────────────────────────────────────────────────
  contextLabel: {
    fontFamily: fonts.sans,
    fontSize: type.size.xs,
    fontWeight: type.weight.bold,
    color: colors.fgSecondary,
    letterSpacing: 1,
  },
  subtitleText: {
    fontFamily: fonts.serif,
    fontSize: type.size.lg,
    fontWeight: type.weight.bold,
    color: colors.fg,
    lineHeight: type.leading.snug,
    marginTop: 4,
  },
  dateRangeText: {
    fontFamily: fonts.sans,
    fontSize: type.size.sm,
    color: colors.fgMuted,
    marginTop: 4,
  },

  // ── Stat cards ────────────────────────────────────────────────────────────
  statsRow: {
    flexDirection: 'row',
    gap: 6,
  },
  statCard: {
    flex: 1,
    borderWidth: 0.5,
    borderColor: colors.lineSubtle,
    borderRadius: 3,
    padding: 8,
  },
  statLabel: {
    fontFamily: fonts.sans,
    fontSize: type.size['2xs'],
    fontWeight: type.weight.bold,
    color: colors.fgSecondary,
    letterSpacing: 0.8,
    marginBottom: 4,
  },
  statValue: {
    fontFamily: fonts.sans,
    fontSize: type.size.md,
    fontWeight: type.weight.bold,
    color: colors.fg,
  },
  statSub: {
    fontFamily: fonts.sans,
    fontSize: type.size['2xs'],
    color: colors.fgMuted,
    marginTop: 2,
  },

  // ── Section label ─────────────────────────────────────────────────────────
  sectionLabel: {
    fontFamily: fonts.sans,
    fontSize: type.size.xs,
    fontWeight: type.weight.bold,
    color: colors.fgSecondary,
    letterSpacing: 1,
    marginBottom: 6,
  },

  // ── Table ─────────────────────────────────────────────────────────────────
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: colors.fill,
    borderTopLeftRadius:  2,
    borderTopRightRadius: 2,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: colors.lineSubtle,
  },
  tableRowAlt: {
    backgroundColor: colors.surface,
  },
  noDataText: {
    fontFamily: fonts.sans,
    fontSize: type.size.sm,
    color: colors.fgMuted,
    paddingVertical: 10,
    paddingHorizontal: 8,
  },

  // ── Footer ────────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 24,
    left:  pageGeometry.marginLeft,
    right: pageGeometry.marginRight,
  },
  footerRow: {
    flexDirection: 'row',
    marginTop: 4,
  },
  footerText: {
    fontFamily: fonts.sans,
    fontSize: type.size['2xs'],
    color: colors.fgMuted,
  },
  footerRight: {
    fontFamily: fonts.sans,
    fontSize: type.size['2xs'],
    color: colors.fgMuted,
    flexGrow: 1,
    textAlign: 'right',
  },
})

// ── Table ─────────────────────────────────────────────────────────────────────

function PdfTable({
  columns,
  rows,
}: {
  columns: ReportColumn[]
  rows: Array<Record<string, string | number>>
}) {
  if (rows.length === 0) {
    return <Text style={s.noDataText}>No data available.</Text>
  }
  return (
    <View>
      {/* Header row */}
      <View style={s.tableHeaderRow}>
        {columns.map(col => (
          <Text
            key={col.key}
            style={{
              flex: col.flex ?? 1,
              paddingHorizontal: 8,
              paddingVertical: 5,
              fontFamily: fonts.sans,
              fontSize: type.size['2xs'],
              fontWeight: type.weight.bold,
              color: colors.fgSecondary,
              letterSpacing: 0.8,
              textAlign: col.align ?? 'left',
            }}
          >
            {col.label.toUpperCase()}
          </Text>
        ))}
      </View>
      {/* Body rows */}
      {rows.map((row, i) => (
        <View key={i} style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}>
          {columns.map((col, ci) => (
            <Text
              key={col.key}
              style={{
                flex: col.flex ?? 1,
                paddingHorizontal: 8,
                paddingVertical: 4,
                fontFamily: fonts.sans,
                fontSize: type.size.sm,
                color: ci === 0 ? colors.fg : colors.fgSecondary,
                textAlign: col.align ?? 'left',
              }}
            >
              {String(row[col.key] ?? '')}
            </Text>
          ))}
        </View>
      ))}
    </View>
  )
}

// ── Stat cards ────────────────────────────────────────────────────────────────

function StatCards({ stats }: { stats: ReportStat[] }) {
  if (stats.length === 0) return null
  return (
    <View style={s.statsRow}>
      {stats.map((stat, i) => (
        <View key={i} style={s.statCard}>
          <Text style={s.statLabel}>{stat.label.toUpperCase()}</Text>
          <Text style={s.statValue}>{stat.value}</Text>
          {stat.sub && <Text style={s.statSub}>{stat.sub}</Text>}
        </View>
      ))}
    </View>
  )
}

// ── Document ──────────────────────────────────────────────────────────────────

export function ReadershipsReport({ data, generatedAt }: ReadershipsReportProps) {
  return (
    <Document
      title={`Readership Metrics – ${data.viewLabel}`}
      author={brand.name}
      subject={`${data.viewLabel}${data.subtitle ? ` · ${data.subtitle}` : ''}`}
      creator={`${brand.name} ${brand.tagline}`}
    >
      <Page size={pageGeometry.size} style={s.page}>

        {/* ── Fixed footer – repeats on every page ── */}
        <View style={s.footer} fixed>
          <Rule />
          <View style={s.footerRow}>
            <Text style={s.footerText}>
              {`${brand.name} ${brand.tagline}  ·  Readership Metrics  ·  ${fmtDate(generatedAt)}`}
            </Text>
            <Text
              style={s.footerRight}
              render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            />
          </View>
        </View>

        {/* ── Header ── */}
        <View style={s.headerRow}>
          <View style={s.brandLockup}>
            <WireLogo height={22} />
            <Text style={s.brandName}>{brand.name}</Text>
          </View>
          <View style={s.headerRight}>
            <Text style={s.headerTitle}>Readership Metrics</Text>
            <Text style={s.headerDate}>{fmtDate(generatedAt)}</Text>
          </View>
        </View>

        <View style={{ height: 12 }} />
        <Rule accent />
        <View style={{ height: 14 }} />

        {/* ── Context block ── */}
        <Text style={s.contextLabel}>
          {`READERSHIP INSIGHTS  ·  ${data.viewLabel.toUpperCase()}`}
        </Text>
        {data.subtitle && (
          <Text style={s.subtitleText}>{data.subtitle}</Text>
        )}
        <Text style={s.dateRangeText}>{data.dateRange}</Text>

        <View style={{ height: 16 }} />

        {/* ── Stats ── */}
        {data.stats.length > 0 && (
          <>
            <StatCards stats={data.stats} />
            <View style={{ height: 20 }} />
          </>
        )}

        {/* ── Sections ── */}
        {data.sections.map((section, i) => (
          <View key={i} style={{ marginBottom: 20 }}>
            <Rule subtle />
            <View style={{ height: 12 }} />
            <Text style={s.sectionLabel}>{section.label.toUpperCase()}</Text>
            {section.chartImageUrl && (
              <View style={{ marginBottom: 10 }}>
                <Image
                  src={section.chartImageUrl}
                  style={{ width: '100%', height: section.chartHeight ?? 200, objectFit: 'contain' }}
                />
              </View>
            )}
            {section.columns.length > 0 && (
              <PdfTable columns={section.columns} rows={section.rows} />
            )}
          </View>
        ))}

      </Page>
    </Document>
  )
}
